import axiosClient from "../axiosClient";
import {
  isBonusChallenge,
  isQuizChallenge,
  loadChallenges,
  refreshChallenges,
} from "../challenges/api_v1_challenges";
import {
  announceEggReward,
  claimedRewardIsNew,
  hasRewardBaseline,
  rememberClaimedRewards,
  rewardTaskId,
} from "../../utils/eggReward";
import { rememberMythFactOutcome } from "../../utils/mythFactOutcome";
import {
  balanceAfterReward,
  persistEasterBalance,
  readEasterBalance,
} from "../economy/api_v1_economy";
import { writeJsonCache } from "../../utils/sessionListCache";
import {
  CACHE_KEYS,
  SESSION_CACHE_TTL,
  cachedRequest,
  invalidateCachedRequest,
} from "../../utils/apiCache";

export const MYTH_FACT_CACHE_KEY = "ga_myth_fact_cache";

function withBearer() {
  const bearerTokenHeader = sessionStorage.getItem("accessToken");
  axiosClient.defaults.headers.common["Authorization"] =
    `Bearer ${bearerTokenHeader}`;
  return axiosClient;
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

const EGGS_ONCE_KEY = "ga_myth_fact_eggs_once";

function asBool(value) {
  if (value === true || value === 1 || value === "1" || value === "true") return true;
  if (value === false || value === 0 || value === "0" || value === "false") return false;
  return null;
}

function nestedObject(payload) {
  if (
    payload?.data &&
    typeof payload.data === "object" &&
    !Array.isArray(payload.data)
  ) {
    return payload.data;
  }
  return null;
}

function unwrap(payload) {
  if (!payload || typeof payload !== "object") return null;
  const nested = nestedObject(payload);
  if (
    nested &&
    payload.question == null &&
    payload.is_correct == null &&
    payload.success == null
  ) {
    return nested;
  }
  return payload;
}

function answerSource(payload) {
  const root = unwrap(payload);
  if (!root || typeof root !== "object") return null;
  const nested = nestedObject(root);
  // Always merge nested answer payload so eggs_awarded / is_correct are not lost
  // when the outer envelope also has success:true.
  if (
    nested &&
    (nested.is_correct != null ||
      nested.correct_answer != null ||
      nested.eggs_awarded != null ||
      nested.success != null)
  ) {
    return {
      ...root,
      ...nested,
      success:
        root.success === true ||
        nested.success === true ||
        payload?.success === true,
    };
  }
  return root;
}

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function eggsAlreadyCredited(questionId) {
  try {
    const saved = JSON.parse(sessionStorage.getItem(EGGS_ONCE_KEY) || "null");
    return Boolean(
      saved &&
        saved.day === todayKey() &&
        saved.questionId === String(questionId || "")
    );
  } catch {
    return false;
  }
}

function markEggsCredited(questionId) {
  try {
    sessionStorage.setItem(
      EGGS_ONCE_KEY,
      JSON.stringify({
        day: todayKey(),
        questionId: String(questionId || ""),
      })
    );
  } catch {
    /* ignore quota */
  }
}

export function getMythFact(options = {}) {
  return cachedRequest(
    CACHE_KEYS.mythFact,
    () => withBearer().get("/myth-fact"),
    { ttl: SESSION_CACHE_TTL, force: options.force === true },
  );
}

export function postMythFactAnswer(answer) {
  invalidateCachedRequest(CACHE_KEYS.mythFact);
  return withBearer().post("/myth-fact/answer", { answer });
}

function questionText(node) {
  if (!node || typeof node !== "object") return "";
  const value = node.question ?? node.Question;
  if (typeof value === "string" && value.trim()) return value.trim();
  return "";
}

function findMythQuestion(payload) {
  const queue = [payload];
  const seen = new Set();
  while (queue.length) {
    const node = queue.shift();
    if (!node || typeof node !== "object" || seen.has(node)) continue;
    seen.add(node);
    const text = questionText(node);
    if (text) {
      return {
        success: true,
        message: "",
        questionId: node.question_id ?? node.questionId ?? null,
        question: text,
        answered: asBool(node.answered) === true,
        yourAnswer: node.your_answer ?? node.yourAnswer ?? null,
      };
    }
    Object.values(node).forEach((value) => {
      if (value && typeof value === "object") queue.push(value);
    });
  }
  return null;
}

export function normalizeMythFact(payload) {
  let data = payload;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }
  const found = findMythQuestion(data);
  if (found) return found;

  const root = unwrap(data);
  if (!root) return null;
  if (root.success === false) {
    return {
      success: false,
      message: String(root.message || ""),
      questionId: null,
      question: "",
      answered: false,
      yourAnswer: null,
    };
  }
  return null;
}

export function normalizeMythFactAnswer(payload) {
  const root = answerSource(payload);
  if (!root || typeof root !== "object") return null;

  const isCorrect = asBool(root.is_correct);
  return {
    success: root.success === true || isCorrect === true || isCorrect === false,
    message: String(root.message || ""),
    isCorrect,
    correctAnswer: root.correct_answer ?? null,
    eggsAwarded: Math.max(0, toNumber(root.eggs_awarded, 0)),
  };
}

export function resolveMythFactScreen(result) {
  if (!result) return null;
  if (result.isCorrect === true) return "correct";
  if (result.isCorrect === false) return "wrong";
  return null;
}

export async function loadMythFact(options = {}) {
  const response = await getMythFact(options);
  const question = normalizeMythFact(response?.data);
  if (question?.question) {
    writeJsonCache(MYTH_FACT_CACHE_KEY, question);
  }
  return question;
}

async function syncCorrectAnswer(result, questionId) {
  const awarded = Math.max(0, result?.eggsAwarded || 0);
  const canCredit = awarded > 0 && !eggsAlreadyCredited(questionId);
  const before = readEasterBalance();

  if (!hasRewardBaseline()) {
    try {
      const prior = await loadChallenges();
      rememberClaimedRewards(prior?.tasks);
    } catch (error) {
      console.error("Myth fact challenge snapshot failed:", error);
    }
  }

  let loaded = null;
  try {
    loaded = await refreshChallenges();
  } catch (error) {
    console.error("Myth fact challenge sync failed:", error);
  }

  if (canCredit) {
    markEggsCredited(questionId);
    const afterRefresh = readEasterBalance();
    result.eggBalance = persistEasterBalance(
      balanceAfterReward(before, awarded, afterRefresh)
    );
  } else {
    result.eggBalance = readEasterBalance();
  }

  const quiz = (loaded?.tasks || []).find((task) => isQuizChallenge(task));
  const taskEggs = Math.max(0, Number(quiz?.rewardEggs) || 0);
  const eggsToShow = Math.max(awarded, taskEggs);

  // Correct answer → COLLECTED egg modal (title + Easter Eggs + AWESOME).
  if (eggsToShow > 0) {
    announceEggReward({
      title: quiz?.title || "Myth or Fact - today's question",
      rewardEggs: eggsToShow,
      eggBalance: result.eggBalance,
      taskKey: canCredit ? rewardTaskId(quiz) || "myth-fact" : undefined,
    });
  }

  const bonus = (loaded?.tasks || []).find((task) => isBonusChallenge(task));
  if (
    bonus?.claimed &&
    bonus.rewardEggs > 0 &&
    claimedRewardIsNew(bonus)
  ) {
    const afterRefresh = readEasterBalance();
    result.eggBalance = persistEasterBalance(
      balanceAfterReward(
        result.eggBalance ?? before,
        bonus.rewardEggs,
        afterRefresh
      )
    );
    announceEggReward({
      title: bonus.title || "Complete all Tasks",
      rewardEggs: bonus.rewardEggs,
      eggBalance: result.eggBalance,
      taskKey: rewardTaskId(bonus),
    });
  }
  return result;
}

export async function submitMythFactAnswer(answer, questionId) {
  const choice = String(answer || "").trim().toLowerCase();
  if (choice !== "myth" && choice !== "fact") {
    return {
      success: false,
      message: "",
      isCorrect: null,
      correctAnswer: null,
      eggsAwarded: 0,
    };
  }

  let result;
  try {
    const response = await postMythFactAnswer(choice);
    result = normalizeMythFactAnswer(response?.data);
  } catch (error) {
    const data = error?.response?.data;
    if (data && typeof data === "object") {
      result = normalizeMythFactAnswer(data);
    } else {
      throw error;
    }
  }

  if (result) rememberMythFactOutcome(result.isCorrect === true);

  if (result?.isCorrect === true) {
    return syncCorrectAnswer(result, questionId);
  }
  if (result?.isCorrect === false) {
    try {
      await refreshChallenges();
    } catch (error) {
      console.error("Myth fact challenge sync failed:", error);
    }
  }
  return result;
}
